import {
  IMPORT_EMAIL_RE,
  IMPORT_MAX_PEOPLE_PER_ROW,
  cleanImportPhone,
  normalizeHeader,
  parseImportBoolean,
  parseImportDate,
  parseImportInteger,
  parseImportMoneyCents,
  splitPersonName,
  type CompanyImportRow,
  type ImportPerson,
} from "./import.js";

/**
 * Formato "Atoka": export Excel di liste aziendali italiane (atoka.io).
 *
 * Una riga = un'azienda, con anagrafica camerale, recapiti divisi per
 * reparto e fino a tre esponenti con il relativo ruolo. Il file viene
 * riconosciuto dalle intestazioni e mappato senza intervento dell'utente;
 * gli esponenti diventano contatti collegati all'azienda.
 */

export const ATOKA_SOURCE = "atoka";

// ── intestazioni ─────────────────────────────────────────────────────

/** chiave semantica → intestazioni accettate (già normalizzate) */
const COLUMNS = {
  sourceUrl: ["url atoka"],
  fiscalCode: ["codice fiscale"],
  vatNumber: ["partita iva"],
  reaNumber: ["numero rea"],
  reaProvince: ["provincia rea"],
  name: ["ragione sociale"],
  legalForm: ["natura giuridica"],
  foundedOn: ["sul mercato da"],
  isStartup: ["startup innovativa"],
  isOperational: ["operativa"],
  atecoCode: ["codice ateco 2007", "codice ateco"],
  atecoDescription: ["descrizione ateco 2007", "descrizione ateco"],
  naceCode: ["codice nace"],
  naceDescription: ["descrizione nace"],
  address: ["indirizzo completo sede legale", "indirizzo completo"],
  zip: ["c a p sede legale", "cap sede legale"],
  city: ["comune sede legale"],
  province: ["provincia sede legale"],
  region: ["regione sede legale"],
  geoArea: ["area geografica sede legale"],
  localUnits: ["numero unita locali oltre alla sede legale", "numero unita locali"],
  otherUnits: ["altre unita locali"],
  phonesVerified: ["telefoni verificati"],
  phones: ["telefoni"],
  website: ["website", "sito web"],
  employees: ["numero dipendenti"],
  revenue: ["ricavi"],
  balanceDate: ["data chiusura bilancio"],
  isListed: ["quotata"],
  peopleUrl: ["dettagli persone"],
  cms: ["cms"],
  ecommerce: ["ecommerce"],
} as const satisfies Record<string, readonly string[]>;

type ColumnKey = keyof typeof COLUMNS;

/** ordine di preferenza dell'indirizzo email "ufficiale" dell'azienda */
const EMAIL_CATEGORIES = [
  "info",
  "direzione",
  "vendite",
  "amministrazione",
  "marketing",
  "acquisti",
  "assistenza",
  "tecnico",
  "altre",
] as const;

/** Atoka esporta gli esponenti come colonne ordinali affiancate al ruolo */
const PERSON_ORDINALS = ["primo", "secondo", "terzo", "quarto", "quinto", "sesto"] as const;

/** intestazioni che, insieme, identificano un export Atoka */
const SIGNATURE = [
  "codice ateco 2007",
  "primo esponente",
  "dettagli persone",
  "natura giuridica",
  "sul mercato da",
  "telefoni verificati",
  "area geografica sede legale",
  "indirizzo completo sede legale",
];

export function detectAtokaFormat(headers: readonly string[]): boolean {
  const set = new Set(headers.map(normalizeHeader));
  if (set.has("url atoka")) return true;
  if (!set.has("ragione sociale")) return false;
  return SIGNATURE.filter((h) => set.has(h)).length >= 3;
}

/** indice di colonna per ogni intestazione normalizzata (prima vince) */
function indexHeaders(headers: readonly string[]): Map<string, number> {
  const map = new Map<string, number>();
  headers.forEach((header, i) => {
    const key = normalizeHeader(header);
    if (key && !map.has(key)) map.set(key, i);
  });
  return map;
}

// ── mappatura mostrata nel wizard ────────────────────────────────────

/** destinazione di una colonna: campo Kairos, persone collegate o campo personalizzato */
export type AtokaTarget = string;

export interface AtokaColumnMapping {
  columnIndex: number;
  header: string;
  /** chiave del campo Kairos, oppure "people" / "custom" / "ignored" */
  target: AtokaTarget;
}

/** colonne che finiscono nei campi personalizzati invece che in una colonna dedicata */
const CUSTOM_COLUMNS: ColumnKey[] = ["otherUnits", "peopleUrl"];

export function atokaColumnMapping(headers: readonly string[]): AtokaColumnMapping[] {
  const byHeader = new Map<string, AtokaTarget>();

  for (const [key, aliases] of Object.entries(COLUMNS) as [ColumnKey, readonly string[]][]) {
    const target = CUSTOM_COLUMNS.includes(key) ? "custom" : atokaFieldKey(key);
    for (const alias of aliases) byHeader.set(alias, target);
  }
  for (const category of EMAIL_CATEGORIES) {
    byHeader.set(`email verificate ${category}`, "email");
    byHeader.set(`email ${category}`, "email");
  }
  for (const ordinal of PERSON_ORDINALS) {
    byHeader.set(`${ordinal} esponente`, "people");
    byHeader.set(`ruolo ${ordinal} esponente`, "people");
  }

  return headers.map((header, columnIndex) => ({
    columnIndex,
    header,
    target: byHeader.get(normalizeHeader(header)) ?? "ignored",
  }));
}

/** nome del campo Kairos corrispondente alla colonna Atoka */
function atokaFieldKey(key: ColumnKey): string {
  if (key === "phonesVerified" || key === "phones") return "phone";
  if (key === "revenue") return "revenueCents";
  return key;
}

// ── costruzione della riga ───────────────────────────────────────────

const MAX_CUSTOM_VALUE = 1000;

/**
 * Trasforma una riga del file Atoka nel payload accettato da
 * POST /api/companies/import (persone collegate incluse).
 */
export function buildAtokaCompanyRow(
  headers: readonly string[],
  cells: readonly string[],
  rowNumber: number,
): CompanyImportRow {
  const index = indexHeaders(headers);
  const at = (header: string): string => {
    const i = index.get(header);
    const value = i == null ? "" : (cells[i] ?? "");
    return value.trim();
  };
  const get = (key: ColumnKey): string => {
    for (const alias of COLUMNS[key]) {
      const value = at(alias);
      if (value) return value;
    }
    return "";
  };

  // ── recapiti ──────────────────────────────────────────────────────
  const phones = uniqueList([...splitList(get("phonesVerified")), ...splitList(get("phones"))])
    .map(cleanImportPhone)
    .filter(Boolean);

  const emailsByCategory = new Map<string, string[]>();
  const allEmails: string[] = [];
  for (const category of EMAIL_CATEGORIES) {
    const verified = splitList(at(`email verificate ${category}`));
    const plain = splitList(at(`email ${category}`));
    const valid = uniqueList([...verified, ...plain])
      .map((v) => v.toLowerCase())
      .filter((v) => IMPORT_EMAIL_RE.test(v));
    emailsByCategory.set(category, valid);
    allEmails.push(...valid);
  }
  const companyEmail =
    EMAIL_CATEGORIES.map((c) => emailsByCategory.get(c)?.[0]).find(Boolean) ?? null;

  // ── indirizzo ─────────────────────────────────────────────────────
  const address = get("address");
  const province = provinceCode(address, get("province"), get("reaProvince"));

  // ── persone collegate ─────────────────────────────────────────────
  const people = extractPeople(at, uniqueList(allEmails), phones[0] ?? null);

  // ── quel che non ha una colonna dedicata ──────────────────────────
  const custom: Record<string, string> = {};
  const usedEmails = new Set(people.map((p) => p.email).filter(Boolean) as string[]);
  if (companyEmail) usedEmails.add(companyEmail);
  const otherEmails = uniqueList(allEmails).filter((e) => !usedEmails.has(e));
  if (phones.length > 1) custom["Altri telefoni"] = clip(phones.slice(1).join(", "));
  if (otherEmails.length) custom["Altre email"] = clip(otherEmails.join(", "));
  if (get("otherUnits")) custom["Altre unità locali"] = clip(get("otherUnits"));
  if (get("peopleUrl")) custom["Dettagli persone (Atoka)"] = clip(get("peopleUrl"));

  const atecoDescription = text(get("atecoDescription"));

  return {
    rowNumber,
    name: text(get("name")),
    vatNumber: text(get("vatNumber")) ?? text(get("fiscalCode")),
    website: text(get("website")),
    industry: atecoDescription,
    email: companyEmail,
    phone: phones[0] ?? null,
    address: text(address),
    city: text(get("city")),
    zip: text(get("zip")),
    province,
    country: "IT",
    fiscalCode: text(get("fiscalCode")),
    reaNumber: text(get("reaNumber")),
    reaProvince: text(get("reaProvince")),
    legalForm: text(get("legalForm")),
    foundedOn: parseImportDate(get("foundedOn")),
    atecoCode: text(get("atecoCode")),
    atecoDescription,
    naceCode: text(get("naceCode")),
    naceDescription: text(get("naceDescription")),
    region: text(get("region")),
    geoArea: text(get("geoArea")),
    employees: parseImportInteger(get("employees")),
    revenueCents: parseImportMoneyCents(get("revenue")),
    balanceDate: parseImportDate(get("balanceDate")),
    localUnits: parseImportInteger(get("localUnits")),
    isStartup: parseImportBoolean(get("isStartup")),
    isOperational: parseImportBoolean(get("isOperational")),
    isListed: parseImportBoolean(get("isListed")),
    cms: text(get("cms")),
    ecommerce: text(get("ecommerce")),
    source: ATOKA_SOURCE,
    sourceUrl: text(get("sourceUrl")),
    custom,
    people,
  };
}

/** Gli esponenti con il loro ruolo, arricchiti con l'email più plausibile. */
function extractPeople(
  at: (header: string) => string,
  emails: readonly string[],
  fallbackPhone: string | null,
): ImportPerson[] {
  const people: ImportPerson[] = [];
  const taken = new Set<string>();

  for (const ordinal of PERSON_ORDINALS) {
    if (people.length >= IMPORT_MAX_PEOPLE_PER_ROW) break;
    const fullName = at(`${ordinal} esponente`);
    if (!fullName) continue;
    const { firstName, lastName } = splitPersonName(fullName);
    if (!firstName) continue;

    const email = matchPersonEmail(firstName, lastName, emails, taken);
    if (email) taken.add(email);

    people.push({
      firstName: text(firstName),
      lastName: text(lastName),
      jobTitle: text(formatRole(at(`ruolo ${ordinal} esponente`))),
      email,
      // Atoka non espone i recapiti diretti: resta il centralino aziendale
      phone: email ? null : fallbackPhone,
      mobile: null,
    });
  }
  return people;
}

/**
 * Associa a una persona l'indirizzo aziendale che ne contiene il nome
 * ("giorgio.striano@…", "g.striano@…"). Nessun match → nessuna email:
 * meglio un campo vuoto di un indirizzo di un'altra persona.
 */
export function matchPersonEmail(
  firstName: string,
  lastName: string,
  emails: readonly string[],
  taken: ReadonlySet<string> = new Set(),
): string | null {
  const first = asciiLetters(firstName.split(/\s+/)[0] ?? "");
  const lastWords = lastName.split(/\s+/).map(asciiLetters).filter(Boolean);
  const last = lastWords[lastWords.length - 1] ?? "";
  if (!first || !last) return null;

  let best: { email: string; score: number } | null = null;
  for (const email of emails) {
    if (taken.has(email)) continue;
    if (!IMPORT_EMAIL_RE.test(email)) continue;
    const local = asciiLower(email.split("@")[0] ?? "");
    const compact = local.replace(/[^a-z]/g, "");
    const tokens = local.split(/[^a-z]+/).filter(Boolean);

    const initial = first[0]!;
    let score = 0;
    if (compact.includes(first + last) || compact.includes(last + first)) score = 100;
    else if (tokens.includes(first) && tokens.includes(last)) score = 95;
    else if (tokens.includes(last) && (tokens.includes(initial) || compact.startsWith(initial + last)))
      score = 80;
    else if (last.length >= 5 && tokens.includes(last)) score = 70;
    else if (last.length >= 6 && compact.includes(last)) score = 60;

    if (score >= 60 && (!best || score > best.score)) best = { email, score };
  }
  return best?.email ?? null;
}

// ── utilità ──────────────────────────────────────────────────────────

/** Atoka usa uno spazio singolo per le celle vuote. */
function text(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  return v ? v : null;
}

function splitList(value: string): string[] {
  return value
    .split(/[,;]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function uniqueList(values: readonly string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function clip(value: string): string {
  return value.length > MAX_CUSTOM_VALUE ? value.slice(0, MAX_CUSTOM_VALUE) : value;
}

/** "amministratore delegato,consigliere" → "Amministratore delegato, Consigliere" */
function formatRole(value: string): string {
  return splitList(value)
    .map((role) => role.charAt(0).toUpperCase() + role.slice(1))
    .join(", ")
    .slice(0, 200);
}

/**
 * La sigla provinciale è in coda all'indirizzo completo ("… Agordo (BL)").
 * In mancanza si ripiega sulla provincia REA e poi sul nome esteso.
 */
function provinceCode(address: string, provinceName: string, reaProvince: string): string | null {
  const fromAddress = /\(([A-Za-z]{2})\)\s*$/.exec(address.trim());
  if (fromAddress) return fromAddress[1]!.toUpperCase();
  if (/^[A-Za-z]{2}$/.test(reaProvince.trim())) return reaProvince.trim().toUpperCase();
  return text(provinceName);
}

function asciiLower(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function asciiLetters(value: string): string {
  return asciiLower(value).replace(/[^a-z]/g, "");
}
